import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { receiveReceipt } from '../../../../../features/receipts/receiptsThunks';
import {
  buildReceivePayload,
  initialCounts,
  isLotFixed,
  isSlotFixed,
  missingOnLine,
  pendingOf,
  receiveProblem,
} from '../../../../../features/receipts/receiptsUtils';
import { fetchSlotOptions } from '../../../../../features/slots/slotsThunks';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

const NO_SLOT = 'none';

/**
 * Counts what arrived of an order, box in hand. This is what writes to the
 * kardex: exactly what was counted goes in — not what was declared, not what
 * is missing.
 *
 * The landing location is chosen here and only among receiving zones, and it is
 * fixed by the first count. Lot and expiry are the real ones, and they are
 * demanded when the product's own flags demand them.
 *
 * @param {object} props
 * @param {object|null} props.receipt - The order being received, with its
 *   lines; `null` keeps the dialog closed.
 * @param {() => void} props.onClose
 * @param {(notice: {type: string, message: string}) => void} props.onDone
 */
export const ReceiveReceiptModal = ({ receipt, onClose, onDone }) => {
  const dispatch = useDispatch();
  const isSaving = useSelector((state) => state.receipts.acting === 'receive');
  const { options: slots, optionsWarehouseId } = useSelector((state) => state.slots);

  const [counts, setCounts] = useState({});
  const [slotId, setSlotId] = useState(null);
  const [saveError, setSaveError] = useState(null);

  const isOpen = Boolean(receipt);
  const receiptId = receipt?.id;
  const warehouseId = receipt?.warehouse;

  // Seeded on each opening: every line starts on what is still pending.
  useEffect(() => {
    if (!receipt) return;
    setCounts(initialCounts(receipt));
    setSlotId(receipt.slot || null);
    setSaveError(null);
    // The order object is replaced by every reload of the listing behind this
    // dialog; reseeding on that would wipe what is being typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptId]);

  useEffect(() => {
    if (warehouseId) dispatch(fetchSlotOptions(warehouseId));
  }, [warehouseId, dispatch]);

  const receivingSlots =
    optionsWarehouseId === warehouseId
      ? slots.filter((slot) => slot.zone_type === 'receiving')
      : [];
  const slotFixed = isSlotFixed(receipt);

  // With a dock to land on, the first one is offered rather than nothing.
  const firstReceivingSlotId = receivingSlots[0]?.id ?? null;
  useEffect(() => {
    if (!isOpen || slotFixed || slotId || !firstReceivingSlotId) return;
    setSlotId(firstReceivingSlotId);
  }, [isOpen, slotFixed, slotId, firstReceivingSlotId]);

  if (!receipt) return null;

  const setCount = (lineId, patch) =>
    setCounts((prev) => ({ ...prev, [lineId]: { ...prev[lineId], ...patch } }));

  const slotOptions = slotFixed
    ? [{ value: receipt.slot, label: receipt.slot_name }]
    : receivingSlots.length > 0
      ? receivingSlots.map((slot) => ({ value: slot.id, label: slot.name }))
      : [{ value: NO_SLOT, label: 'Sin ubicación' }];

  const slotHint = (() => {
    if (slotFixed) {
      return `Fijada con el primer conteo: el resto de ${receipt.code} aterriza en ${receipt.slot_name}.`;
    }
    return receivingSlots.length > 0
      ? 'Donde queda la carga hasta que se guarde. Solo zonas de recepción.'
      : 'Esta bodega no tiene ninguna ubicación de recepción: la carga queda sin ubicación.';
  })();

  const problem = receiveProblem(receipt, counts);

  const handleConfirm = async () => {
    setSaveError(null);
    const action = await dispatch(
      receiveReceipt({ receiptId: receipt.id, payload: buildReceivePayload(receipt, counts, slotId) })
    );

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    // A location left over its capacity is said, not blocked: the goods are
    // physically there.
    const warnings = action.payload.capacityWarnings;
    onDone({
      type: 'success',
      message: [
        `${receipt.code}: el stock entró a la zona de recepción.`,
        ...warnings,
      ].join(' '),
    });
    onClose();
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="lg"
      title={`Recepcionar ${receipt.code}`}
      testId="receipt-receive-modal"
    >
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6">
        Cuenta lo que llegó en esta entrega. El lote y el vencimiento que escribas son los reales.
        Al confirmar entra al stock exactamente lo contado.
      </p>

      <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
        {saveError && (
          <div
            data-testid="receipt-receive-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        <div>
          <FilterableSelect
            variant="form"
            testId="receipt-receive-slot"
            label="Ubicación de recepción"
            value={slotFixed ? receipt.slot : slotId ?? NO_SLOT}
            onChange={(value) => setSlotId(value === NO_SLOT ? null : value)}
            options={slotOptions}
            searchPlaceholder="Buscar ubicación..."
            disabled={slotFixed || isSaving || receivingSlots.length === 0}
          />
          <p data-testid="receipt-receive-slot-hint" className="text-text-muted text-xs mt-1">
            {slotHint}
          </p>
        </div>

        {receipt.lines.map((line) => {
          const count = counts[line.id] || {};
          const pending = pendingOf(line);
          const missing = missingOnLine(line, count);
          const lotFixed = isLotFixed(line);
          const isOver = Number(count.quantity) > pending;

          return (
            <div
              key={line.id}
              data-testid="receipt-receive-line"
              data-line-id={line.id}
              className="rounded-xl border border-border bg-ninja-gray/40 dark:bg-[#111] p-3 space-y-3"
            >
              <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                <div className="min-w-0">
                  <p className="font-bold font-mono text-sm text-ninja-dark dark:text-white">
                    {line.product_sku}
                  </p>
                  <p className="text-xs text-text-muted truncate">{line.product_name}</p>
                </div>
                <p className="text-xs text-text-muted whitespace-nowrap">
                  Esperado {formatQuantity(line.expected)} · Ya recibido{' '}
                  {formatQuantity(line.received)} · Pendiente {formatQuantity(pending)}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Field
                  label="Recibido ahora"
                  htmlFor={`receive-quantity-${line.id}`}
                  // More than declared can arrive, and it is received: it is
                  // said, not refused.
                  hint={isOver ? 'Más de lo pendiente' : undefined}
                >
                  <input
                    id={`receive-quantity-${line.id}`}
                    type="number"
                    min="0"
                    step="any"
                    data-testid={`receive-quantity-${line.id}`}
                    value={count.quantity ?? ''}
                    onChange={(event) => setCount(line.id, { quantity: event.target.value })}
                    disabled={isSaving}
                    className={inputClass}
                  />
                </Field>

                <Field
                  label={line.lot_controlled ? 'Lote *' : 'Lote'}
                  htmlFor={`receive-lot-${line.id}`}
                  error={missing.lot ? 'Obligatorio: el producto controla lote' : undefined}
                  hint={
                    lotFixed
                      ? 'Fijado con el primer conteo'
                      : line.lot_controlled
                        ? 'El producto controla lote'
                        : undefined
                  }
                >
                  <input
                    id={`receive-lot-${line.id}`}
                    type="text"
                    data-testid={`receive-lot-${line.id}`}
                    value={count.lot ?? ''}
                    onChange={(event) => setCount(line.id, { lot: event.target.value })}
                    // One line carries one lot: once it received with it, it keeps it.
                    disabled={isSaving || lotFixed}
                    className={inputClass}
                  />
                </Field>

                <Field
                  label={line.expiry_controlled ? 'Vencimiento *' : 'Vencimiento'}
                  htmlFor={`receive-expiry-${line.id}`}
                  error={
                    missing.expiry ? 'Obligatorio: el producto controla vencimiento' : undefined
                  }
                  hint={line.expiry_controlled ? 'El producto controla vencimiento' : undefined}
                >
                  <input
                    id={`receive-expiry-${line.id}`}
                    type="date"
                    data-testid={`receive-expiry-${line.id}`}
                    value={count.expiry_date ?? ''}
                    onChange={(event) => setCount(line.id, { expiry_date: event.target.value })}
                    disabled={isSaving}
                    className={inputClass}
                  />
                </Field>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing. */}
        <Button
          variant="primary"
          loading={isSaving}
          loadingLabel="Confirmando..."
          disabled={Boolean(problem)}
          onClick={handleConfirm}
          data-testid="receipt-receive-confirm"
        >
          {problem || 'Confirmar recepción'}
        </Button>
      </div>
    </Modal>
  );
};
